type EventsShape = Record<
  string,
  Record<string, { name: string; properties: readonly string[]; optionalProperties?: readonly string[] }>
>;

const BuildersGlobalProperties = ['user_id', 'account_id', 'workspace_id', 'account_plan_type'] as const;
export type BuildersGlobalProperty = (typeof BuildersGlobalProperties)[number];

// Shared by every agentUsage event; built by useAgentUsageCommonEventProps in features/billing-usage.
const AgentUsageCommonProperties = [
  'Used Percent Bucket',
  'Projected Percent Bucket',
  'Enforcing',
  'Beta',
  'Account Id',
  'Role',
  'Is Standard Role',
] as const;
export type AgentUsageCommonProperty = (typeof AgentUsageCommonProperties)[number];

export const Events = {
  renderer: {
    // The workspace default-template-engine ("liquid version") upgrade/downgrade flow. Values for
    // 'Current Version'/'Version' are the engine name ('ruby' for legacy, 'liquidjs' for latest).
    versionChangeStarted: {
      name: 'Renderer: Version Change Started',
      properties: ['Current Version', 'Workspace Setting'] as const,
    },
    versionChangeCompleted: {
      name: 'Renderer: Version Change Completed',
      properties: ['Version', 'Workspace Setting'] as const,
    },
  },
  role: {
    createStarted: {
      name: 'Role: Custom Role Create Started',
      properties: [] as const,
    },
    duplicateStarted: {
      name: 'Role: Duplicate Started',
      properties: ['Role ID', 'Role Name', 'Role Type', 'Permissions Count'] as const,
    },
    deleteStarted: {
      name: 'Role: Custom Role Delete Started',
      properties: ['Role ID', 'Role Name', 'Role Type', 'Permissions Count'] as const,
    },
  },
  // Agent usage meter events for the pricing-experiment learning plan (TAG-1927).
  // Every event carries the shared slice properties so the analysis can split by
  // usage bucket, 1A/1B enforcement era, and role without per-event fixes.
  agentUsage: {
    planCardViewed: {
      name: 'Agent Usage: Plan Card Viewed',
      properties: [...AgentUsageCommonProperties, 'Cycle Day'] as const,
    },
    aiUsageTabViewed: {
      name: 'Agent Usage: AI Usage Tab Viewed',
      properties: [...AgentUsageCommonProperties, 'Active Users', 'People Rows Shown', 'Folded Rows'] as const,
    },
    unitToggled: {
      name: 'Agent Usage: Unit Toggled',
      properties: [...AgentUsageCommonProperties, 'To Unit'] as const,
    },
    chatPillShown: {
      name: 'Agent Usage: Chat Pill Shown',
      properties: [...AgentUsageCommonProperties, 'Critical'] as const,
    },
    chatPillTooltipOpened: {
      name: 'Agent Usage: Chat Pill Tooltip Opened',
      properties: [...AgentUsageCommonProperties] as const,
    },
    threadCardShown: {
      name: 'Agent Usage: Thread Card Shown',
      properties: [...AgentUsageCommonProperties, 'Variant'] as const,
    },
  },
  aiAssistant: {
    opened: {
      name: 'AI Assistant: Opened',
      properties: ['Trigger Source'] as const,
    },
    closed: {
      name: 'AI Assistant: Closed',
      properties: [] as const,
    },
    transferredToTsChat: {
      name: 'AI Assistant: Transferred to TS Chat',
      properties: ['Message Count', 'Had Tool Calls'] as const,
    },
    transferredToTsSupportForm: {
      name: 'AI Assistant: Transferred to TS Support Form',
      properties: ['Message Count', 'Had Tool Calls'] as const,
    },
    transferredToAccountManagement: {
      name: 'AI Assistant: Transferred to Account Management',
      properties: ['Account Id', 'Is Premium', 'Selected Options', 'Message Count', 'Had Tool Calls'] as const,
    },
    suggestionClicked: {
      name: 'AI Assistant: Suggestion Clicked',
      properties: ['Index', 'Click Location', 'Conversation ID'] as const,
      // Index alone can't identify a suggestion — the pool is shuffled per render,
      // so position 1 is a different chip on every impression.
      // Suggestion Group aggregates chips that pitch one capability through
      // several rotating labels — without it, analysis has to union label strings.
      optionalProperties: ['Suggestion Label', 'Suggestion Group', 'Route Group', 'Suggestion Source'] as const,
    },
    promptCardClicked: {
      name: 'AI Assistant: Prompt Card Clicked',
      properties: ['Title', 'Source', 'Position'] as const,
    },
    newChatClicked: {
      name: 'AI Assistant: New Chat Clicked',
      properties: [] as const,
    },
    viewedHistory: {
      name: 'AI Assistant: Viewed History',
      properties: [] as const,
    },
    historyItemClicked: {
      name: 'AI Assistant: History Item Clicked',
      properties: [] as const,
    },
    historyItemRenamed: {
      name: 'AI Assistant: History Item Renamed',
      properties: [] as const,
    },
    historyItemDeleted: {
      name: 'AI Assistant: History Item Deleted',
      properties: [] as const,
    },
    feedbackLinkClicked: {
      name: 'AI Assistant: Feedback Link Clicked',
      properties: [] as const,
    },
    feedbackFormSubmitted: {
      name: 'AI Assistant: Feedback Form Submitted',
      properties: ['Feedback'] as const,
    },
    referencePaginated: {
      name: 'AI Assistant: Reference Paginated',
      properties: ['Direction', 'Current Index', 'Total References'] as const,
    },
    // Impression event — the denominator for suggestionClicked. Distinct from
    // `suggestionsShown`, which fires when the user un-hides the sidebar list.
    suggestionsDisplayed: {
      name: 'AI Assistant: Suggestions Displayed',
      properties: ['Click Location', 'Route Group', 'Suggestion Source', 'Suggestion Labels', 'Count'] as const,
    },
    suggestionsShown: {
      name: 'AI Assistant: Suggestions Shown',
      properties: [] as const,
    },
    suggestionsHidden: {
      name: 'AI Assistant: Suggestions Hidden',
      properties: [] as const,
    },
    editBusinessProfileClicked: {
      name: 'AI Assistant: Edit Business Profile Clicked',
      properties: ['Click Location'] as const,
    },
    contextUsedPopoverOpened: {
      name: 'AI Assistant: Context Used Popover Opened',
      properties: [] as const,
    },
    contextUsedAccordionClicked: {
      name: 'AI Assistant: Context Used Accordion Clicked',
      properties: ['section'] as const,
    },
    tourStarted: {
      name: 'AI Assistant: Tour Started',
      properties: ['Tour Name'] as const,
    },
    tourDismissed: {
      name: 'AI Assistant: Tour Dismissed',
      properties: ['Tour Name'] as const,
    },
    messageSent: {
      name: 'AI Assistant: Message Sent',
      properties: ['Message', 'Has Files', 'File Count', 'Turn Number', 'Is First Message', 'Conversation ID'] as const,
    },
    stopClicked: {
      name: 'AI Assistant: Stop Clicked',
      properties: [] as const,
    },
    messageCopied: {
      name: 'AI Assistant: Message Copied',
      properties: [] as const,
    },
    thumbsUp: {
      name: 'AI Assistant: Thumbs Up',
      properties: [] as const,
    },
    thumbsDown: {
      name: 'AI Assistant: Thumbs Down',
      properties: ['Feedback Text', 'Has Feedback'] as const,
    },
    fileUploaded: {
      name: 'AI Assistant: File Uploaded',
      properties: ['Count', 'File Types'] as const,
    },
    fileDownloaded: {
      name: 'AI Assistant: File Downloaded',
      properties: ['File Type'] as const,
    },
    expandedToFullScreen: {
      name: 'AI Assistant: Expanded to Full Screen',
      properties: [] as const,
    },
    collapsedFromFullScreen: {
      name: 'AI Assistant: Collapsed from Full Screen',
      properties: [] as const,
    },
    slackPromoCtaClicked: {
      name: 'AI Assistant: Slack Promo CTA Clicked',
      properties: [] as const,
    },
    slackPromoLearnMoreClicked: {
      name: 'AI Assistant: Slack Promo Learn More Clicked',
      properties: [] as const,
    },
    slackPromoDismissed: {
      name: 'AI Assistant: Slack Promo Dismissed',
      properties: [] as const,
    },
    approvalModeChanged: {
      name: 'AI Assistant: Approval Mode Changed',
      properties: ['Mode'] as const,
    },
    webSearchToggled: {
      name: 'AI Assistant: Web Search Toggled',
      properties: ['Enabled'] as const,
    },
    routineSuggestionClicked: {
      name: 'AI Assistant: Routine Suggestion Clicked',
      properties: ['Title'] as const,
    },
    routineEditClicked: {
      name: 'AI Assistant: Routine Edit Clicked',
      properties: [] as const,
    },
    routineSaved: {
      name: 'AI Assistant: Routine Saved',
      properties: [] as const,
    },
    routinePaused: {
      name: 'AI Assistant: Routine Paused',
      properties: [] as const,
    },
    routinePausedFromEmail: {
      name: 'AI Assistant: Routine Paused From Email',
      properties: [] as const,
    },
    routineResumed: {
      name: 'AI Assistant: Routine Resumed',
      properties: [] as const,
    },
    routineDeleted: {
      name: 'AI Assistant: Routine Deleted',
      properties: [] as const,
    },
    skillCreated: {
      name: 'AI Assistant: Skill Created',
      properties: [] as const,
    },
    skillSaved: {
      name: 'AI Assistant: Skill Saved',
      properties: [] as const,
    },
    skillDeleted: {
      name: 'AI Assistant: Skill Deleted',
      properties: [] as const,
    },
    skillCreateWithAgentClicked: {
      name: 'AI Assistant: Skill Create With Agent Clicked',
      properties: [] as const,
    },
    skillWriteManuallyClicked: {
      name: 'AI Assistant: Skill Write Manually Clicked',
      properties: [] as const,
    },
    routineToolCreated: {
      name: 'AI Assistant: Routine Tool Created',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolUpdated: {
      name: 'AI Assistant: Routine Tool Updated',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolDeleted: {
      name: 'AI Assistant: Routine Tool Deleted',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolRun: {
      name: 'AI Assistant: Routine Tool Run',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolListed: {
      name: 'AI Assistant: Routine Tool Listed',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolHistory: {
      name: 'AI Assistant: Routine Tool History',
      properties: ['Action', 'Routine Name'] as const,
    },
    tableExported: {
      name: 'AI Assistant: Table Exported',
      properties: [] as const,
    },
    tableCopied: {
      name: 'AI Assistant: Table Copied',
      properties: [] as const,
    },
    limitReached: {
      name: 'AI Assistant: Limit Reached',
      properties: ['Feature', 'Limit', 'Current Count'] as const,
    },
    capabilityApprovalConfirmed: {
      name: 'AI Assistant: Capability Approval Confirmed',
      properties: ['scopes'] as const,
    },
    capabilityApprovalDenied: {
      name: 'AI Assistant: Capability Approval Denied',
      properties: ['scopes'] as const,
    },
  },
  aiProfile: {
    viewed: {
      name: 'AI Profile: Viewed',
      properties: ['Fields Total', 'Fields AI Populated', 'Fields User Overridden', 'Fields Empty'] as const,
    },
    editIntent: {
      name: 'AI Profile: Edit Intent',
      properties: ['Field'] as const,
    },
    saved: {
      name: 'AI Profile: Saved',
      properties: [
        'Fields Changed Count',
        'Fields Added Count',
        'Fields Edited Count',
        'Fields Cleared Count',
        'Changed Fields',
        'Edit Session Ms',
      ] as const,
    },
    discarded: {
      name: 'AI Profile: Discarded',
      properties: ['Unsaved Fields Changed Count'] as const,
    },
    tonePreviewGetPreviewClicked: {
      name: 'AI Profile: Tone Preview Get Preview Clicked',
      properties: ['Channel', 'Sample Input Customized'] as const,
    },
    tonePreviewCopyOutputClicked: {
      name: 'AI Profile: Tone Preview Copy Output Clicked',
      properties: [] as const,
    },
    tonePreviewShuffleSampleInputClicked: {
      name: 'AI Profile: Tone Preview Shuffle Sample Input Clicked',
      properties: [] as const,
    },
    tonePreviewShowSampleInputToggled: {
      name: 'AI Profile: Tone Preview Show Sample Input Toggled',
      properties: [] as const,
    },
  },
  auditLogs: {
    viewed: {
      name: 'Audit Logs: Viewed',
      properties: ['contextType', 'hasFilters', 'workspaceId'] as const,
    },
    contextChanged: {
      name: 'Audit Logs: Context Changed',
      properties: ['contextType', 'autoSelected'] as const,
    },
    workspaceChanged: {
      name: 'Audit Logs: Workspace Changed',
      properties: ['workspaceId'] as const,
    },
    filtersChanged: {
      name: 'Audit Logs: Filters Changed',
      properties: ['filterCount'] as const,
    },
    exportCreated: {
      name: 'Audit Logs: Export Created',
      properties: ['context', 'contextId', 'startTime', 'endTime', 'objectType'] as const,
    },
    exportFailed: {
      name: 'Audit Logs: Export Failed',
      properties: ['context', 'contextId', 'error'] as const,
    },
  },
  deliverability: {
    viewed: {
      name: 'Deliverability: Viewed',
      properties: ['workspaceId', 'hasUnlimitedAccess'] as const,
    },
  },
  emailClients: {
    viewed: {
      name: 'Email Clients: Viewed',
      properties: ['workspaceId', 'hasUnlimitedAccess'] as const,
    },
    chartMetricChanged: {
      name: 'Email Clients: Chart Metric Changed',
      properties: ['metric'] as const,
    },
  },
  darkMode: {
    colorSchemeChanged: {
      name: 'Dark Mode: Color Scheme Changed',
      properties: ['Color Scheme', 'Previous Color Scheme'] as const,
    },
  },
  universalSearch: {
    opened: {
      name: 'Universal Search: Opened',
      properties: ['Entry Point'] as const,
    },
    itemSelected: {
      name: 'Universal Search: Item Selected',
      properties: [
        'Item Type',
        'Result Kind',
        'Query',
        'Result Position',
        'Results Count',
        'API Results Count',
        'Open in New Tab',
        'Time to First Result Ms',
        'Active Filter',
        'Active Sub Filter',
        'Filters Open',
        'Preview Open',
      ] as const,
    },
    previewOpened: {
      name: 'Universal Search: Preview Opened',
      properties: ['Result Kind', 'Query'] as const,
    },
    previewClosed: {
      name: 'Universal Search: Preview Closed',
      properties: ['Result Kind', 'Query'] as const,
    },
    filterChanged: {
      name: 'Universal Search: Filter Changed',
      properties: ['Filter Key', 'Filter Value', 'Previous Filter', 'Previous Sub Filter', 'Query'] as const,
    },
    filterVisibilityToggled: {
      name: 'Universal Search: Filter Visibility Toggled',
      properties: ['Visible'] as const,
    },
    closed: {
      name: 'Universal Search: Closed',
      properties: ['Reason', 'Had Query', 'Query', 'Time Open Ms', 'Active Filter', 'Active Sub Filter'] as const,
    },
  },
  customer_io_basics: {
    modal_viewed: {
      name: 'Customer.io Basics: Modal Viewed',
      properties: [] as const,
    },
    video_clicked: {
      name: 'Customer.io Basics: Video Clicked',
      properties: ['id'] as const,
    },
    video_started: {
      name: 'Customer.io Basics: Video Started',
      properties: ['id', 'name', 'watched', 'started', 'total'] as const,
    },
    video_watched: {
      name: 'Customer.io Basics: Video Watched',
      properties: ['id', 'name', 'watched', 'started', 'total'] as const,
    },
    video_stopped: {
      name: 'Customer.io Basics: Video Stopped',
      properties: ['id', 'name', 'pauseTime', 'watched', 'started', 'total'] as const,
    },
    video_time_shifted: {
      name: 'Customer.io Basics: Video Time Shifted',
      properties: ['id', 'name', 'seekTo', 'seekFrom'] as const,
    },
    previous_clicked: {
      name: 'Customer.io Basics: Previous Clicked',
      properties: ['id'] as const,
    },
    next_clicked: {
      name: 'Customer.io Basics: Next Clicked',
      properties: ['id'] as const,
    },
  },
  help_center: {
    opened: {
      name: 'Help Center: Help Menu Opened',
      properties: [] as const,
    },
    learnAcademyClicked: {
      name: 'Help Center: Learn with Academy',
      properties: [] as const,
    },
    askCommunityClicked: {
      name: 'Help Center: Ask the Community',
      properties: [] as const,
    },
    platformDocsClicked: {
      name: 'Help Center: Platform Documentation',
      properties: [] as const,
    },
    apiDocsClicked: {
      name: 'Help Center: API Documentation',
      properties: [] as const,
    },
    releaseNotesClicked: {
      name: 'Help Center: Release Notes',
      properties: [] as const,
    },
    connectWithPartnerClicked: {
      name: 'Help Center: Connect with a Partner',
      properties: [] as const,
    },
    feedbackRequestStarted: {
      name: 'Help Center: Send Us Feedback',
      properties: [] as const,
    },
    helpRequestStarted: {
      name: 'Help Center: Get Help With an Issue',
      properties: [] as const,
    },
    aiAssistantClicked: {
      name: 'Help Center: Chat with AI Assistant',
      properties: [] as const,
    },
    chatWithSupportClicked: {
      name: 'Help Center: Chat with Support',
      properties: [] as const,
    },
    systemStatusClicked: {
      name: 'Help Center: System Status',
      properties: [] as const,
    },
    grantSupportAccessClicked: {
      name: 'Help Center: Set Support Access',
      properties: [] as const,
    },
  },
  inline_support: {
    opened: {
      name: 'Inline Support: Opened',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    closed: {
      name: 'Inline Support: Closed',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    supportAccessGranted: {
      name: 'Inline Support: Support Access Granted',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    supportAccessError: {
      name: 'Inline Support: Support Access Error',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    submitClicked: {
      name: 'Inline Support: Submit Clicked',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    submitError: {
      name: 'Inline Support: Submit Error',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    submitSuccess: {
      name: 'Inline Support: Submit Success',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    supportAccessGuidanceShown: {
      name: 'Inline Support: Support Access Guidance Shown',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
  },
  plan_and_billing: {
    supportRequestStarted: {
      name: 'Plan and Billing: Support Request Started',
      properties: ['accountId', 'workspaceId', 'source'] as const,
    },
    supportRequestCompleted: {
      name: 'Plan and Billing: Support Request Completed',
      properties: ['accountId', 'workspaceId', 'comment', 'source'] as const,
    },
  },
  mainNav: {
    toggleCollapsed: {
      name: 'main-nav',
      properties: ['isCollapsed'] as const,
    },
  },
  leftNav: {
    itemClicked: {
      name: 'Left Nav: Item Clicked',
      properties: ['Nav Item', 'Nav Section', 'Is Nested', 'Parent Item', 'Entry Point'] as const,
    },
    customizeModalOpened: {
      name: 'Left Nav: Customize Modal Opened',
      properties: [] as const,
    },
    preferenceChanged: {
      name: 'Left Nav: Preference Changed',
      properties: ['Item ID', 'Section', 'Previous Value', 'New Value', 'Source'] as const,
    },
    moreMenuItemClicked: {
      name: 'Left Nav: More Menu Item Clicked',
      properties: ['Item ID', 'Section', 'URL'] as const,
    },
    collapsed: {
      name: 'Left Nav: Collapsed',
      properties: ['Trigger', 'Session Nav Opens After Collapse'] as const,
    },
    expanded: {
      name: 'Left Nav: Expanded',
      properties: ['Trigger', 'Time Collapsed Ms'] as const,
    },
  },
  performance: {
    hydraInitialLoad: {
      name: 'Performance: Hydra Initial Load',
      properties: [
        'Account Id',
        'Data Center',
        'Route Name',
        'Has Tried To Override Metric',
        'Hydra Boot Start',
        'Hydra Authenticated',
        'Hydra Child App Load Start',
        'Hydra Time To Authenticated',
        'Hydra Time To Child App Load Start',
      ] as const,
    },
  },
  workspaceHealth: {
    tabChanged: {
      name: 'Workspace Performance: Tab Changed',
      properties: ['Tab'] as const,
    },
    viewAllClicked: {
      name: 'Workspace Performance: View All Clicked',
      properties: ['Destination'] as const,
    },
    drawerOpened: {
      name: 'Workspace Performance: Drawer Opened',
      properties: ['Drawer'] as const,
    },
    viewBreakdownClicked: {
      name: 'Workspace Performance: View Breakdown Clicked',
      properties: [] as const,
    },
    recommendationCarouselClicked: {
      name: 'Workspace Performance: Recommendation Carousel Clicked',
      properties: ['Direction'] as const,
    },
    recommendationClicked: {
      name: 'Workspace Performance: Recommendation Clicked',
      properties: ['Recommendation'] as const,
    },
    notificationClicked: {
      name: 'Workspace Performance: Notification Clicked',
      properties: [] as const,
    },
    notificationLinkClicked: {
      name: 'Workspace Performance: Notification Link Clicked',
      properties: [] as const,
    },
    askAgentClicked: {
      name: 'Workspace Performance: Ask Agent Clicked',
      properties: ['Panel'] as const,
      // Set only when the prompt came from a specific alert's own `copilot_prompt`; absent for the
      // workspace-wide prompt.
      optionalProperties: ['Alert'] as const,
    },
  },
  tasks: {
    paginated: {
      name: 'Tasks: Paginated',
      properties: ['Direction'] as const,
    },
    paginationReset: {
      name: 'Tasks: Pagination Reset',
      properties: [] as const,
    },
    viewChanged: {
      name: 'Tasks: View Changed',
      properties: ['View'] as const,
    },
    filterApplied: {
      name: 'Tasks: Filter Applied',
      properties: ['Filter Key', 'Filter Value'] as const,
    },
    priorityChanged: {
      name: 'Tasks: Priority Changed',
      properties: ['Mode', 'Task Type'] as const,
    },
    queueDragMoved: {
      name: 'Tasks: Queue Drag Moved',
      properties: ['Task Type', 'Queue', 'Position'] as const,
    },
    canceled: {
      name: 'Tasks: Canceled',
      properties: ['Task Type'] as const,
    },
  },
  home: {
    viewed: {
      name: 'Home: Viewed',
      properties: ['is_builder', 'is_onboarding'] as const,
    },
    recentCardClicked: {
      name: 'Home: Recent Card Clicked',
      properties: ['Resource Type', 'Position', 'Is Create Action'] as const,
    },
    recentActivityClicked: {
      name: 'Home: Recent Activity Clicked',
      properties: ['Resource Type', 'Action'] as const,
    },
    addFundsClicked: {
      name: 'Home: Clicked Add Funds CTA',
      properties: [] as const,
    },
    trialUpgradeClicked: {
      name: 'Home: Trial Progress Upgrade Clicked',
      properties: ['Account Id'] as const,
    },
    inviteTeammateClicked: {
      name: 'Home: Invite Teammate Clicked',
      properties: [] as const,
    },
    messageInvadersOpened: {
      name: 'Home: Message Invaders Opened',
      properties: [] as const,
    },
    messageInvadersGameOver: {
      name: 'Home: Message Invaders Game Over',
      properties: ['Score', 'Wave', 'Is New High Score'] as const,
    },
    flappyMessageOpened: {
      name: 'Home: Flappy Message Opened',
      properties: [] as const,
    },
    flappyMessageGameOver: {
      name: 'Home: Flappy Message Game Over',
      properties: ['Score', 'Is New High Score'] as const,
    },
    agentInputSubmitted: {
      name: 'Home: Agent Input Submitted',
      properties: [] as const,
    },
    workspaceActivityGraphEdited: {
      name: 'Home: Workspace Activity Graph Edited',
      properties: ['Channel'] as const,
    },
    clickedAnalysisLink: {
      name: 'Home: Clicked Analysis Link',
      properties: [] as const,
    },
    clickedClassicDashboard: {
      name: 'Home: Clicked Classic Dashboard',
      properties: [] as const,
    },
    welcomeBannerDismissed: {
      name: 'Home: Welcome Banner Dismissed',
      properties: [] as const,
    },
    welcomeBannerCtaClicked: {
      name: 'Home: Welcome Banner CTA Clicked',
      properties: [] as const,
    },
    promptCardsShown: {
      name: 'Home: Prompt Cards Shown',
      properties: ['Titles', 'Personalized'] as const,
    },
    auditLogsClicked: {
      name: 'Home: Audit Logs Clicked',
      properties: [] as const,
    },
  },
  builders: {
    addFundsClicked: {
      name: 'Builders: Add Funds Clicked',
      properties: [...BuildersGlobalProperties, 'page_name'] as const,
    },
    apiKeySaved: {
      name: 'Builders: API Key Saved',
      properties: [...BuildersGlobalProperties, 'page_name', 'service_account_id'] as const,
    },
    domainAdded: {
      name: 'Builders: Domain Added',
      properties: [...BuildersGlobalProperties, 'page_name', 'domain', 'domain_id'] as const,
    },
    domainVerified: {
      name: 'Builders: Domain Verified',
      properties: [...BuildersGlobalProperties, 'domain', 'verification_method'] as const,
    },
    userAdded: {
      name: 'Builders: User Added',
      properties: [
        ...BuildersGlobalProperties,
        'page_name',
        'is_test_user',
        'user_count',
        'method',
        'remaining_spots',
        'has_email',
      ] as const,
    },
    messageSent: {
      name: 'Builders: Message Sent',
      properties: [...BuildersGlobalProperties, 'is_test_send', 'channel', 'message_type'] as const,
    },
    upNextClicked: {
      name: 'Builders: Up Next Clicked',
      properties: [...BuildersGlobalProperties, 'page_name', 'step'] as const,
    },
    stepSkipped: {
      name: 'Builders: Step Skipped',
      properties: [...BuildersGlobalProperties, 'page_name', 'step'] as const,
    },
    sectionViewed: {
      name: 'Builders: Onboarding Section Viewed',
      properties: [...BuildersGlobalProperties, 'page_name', 'section'] as const,
    },
    codeSnippetCopied: {
      name: 'Builders: Onboarding Code Snippet Copied',
      properties: [...BuildersGlobalProperties, 'page_name', 'language', 'section'] as const,
    },
    emailTemplateClicked: {
      name: 'Builders: Onboarding Email Template Clicked',
      properties: [...BuildersGlobalProperties, 'page_name', 'email_name'] as const,
    },
    headerCtaClicked: {
      name: 'Builders: Onboarding Header CTA Clicked',
      properties: [...BuildersGlobalProperties, 'page_name'] as const,
    },
    productionKeyGenerated: {
      name: 'Builders: Onboarding Production Key Generated',
      properties: [...BuildersGlobalProperties, 'page_name'] as const,
    },
    keysSwappedConfirmed: {
      name: 'Builders: Onboarding Keys Swapped Confirmed',
      properties: [...BuildersGlobalProperties, 'page_name'] as const,
    },
  },
  // Event names intentionally match the existing credits-purchase events fired
  // from the My Plan settings surface so both report into the same Mixpanel
  // events; page_name distinguishes the source. `optionalProperties` documents
  // the source-varying keys the Your Plan flow sends (billing system, payment
  // method, save-card, etc.) that the Builder sandbox purchase surface omits —
  // `track` accepts them as extra keys beyond the required set.
  creditsPurchase: {
    modalOpened: {
      name: 'Credits Purchase: Modal Opened',
      properties: ['Credit Type', 'Account Id', 'page_name'] as const,
      optionalProperties: ['Current Plan', 'Billing System'] as const,
    },
    modalClosed: {
      name: 'Credits Purchase: Modal Closed',
      properties: ['Credit Type', 'page_name'] as const,
      optionalProperties: ['Had Interaction', 'Billing System'] as const,
    },
    quantityChanged: {
      name: 'Credits Purchase: Quantity Changed',
      properties: ['Credit Type', 'Quantity', 'Total Price Cents', 'page_name'] as const,
      optionalProperties: ['Billing System'] as const,
    },
    paymentMethodChanged: {
      name: 'Credits Purchase: Payment Method Changed',
      properties: ['Credit Type', 'Payment Method', 'page_name'] as const,
    },
    purchaseSubmitted: {
      name: 'Credits Purchase: Purchase Submitted',
      properties: [
        'Credit Type',
        'Account Id',
        'Quantity',
        'Total Price Cents',
        'Tax Amount Cents',
        'Total With Tax Cents',
        'page_name',
      ] as const,
      optionalProperties: ['Current Plan', 'Payment Method', 'Save Card', 'Total Credits', 'Billing System'] as const,
    },
    purchaseSucceeded: {
      name: 'Credits Purchase: Purchase Succeeded',
      properties: [
        'Credit Type',
        'Account Id',
        'Quantity',
        'Total Price Cents',
        'Tax Amount Cents',
        'Total With Tax Cents',
        'page_name',
      ] as const,
      optionalProperties: ['Total Credits', 'Billing System'] as const,
    },
    purchaseFailed: {
      name: 'Credits Purchase: Purchase Failed',
      properties: ['Credit Type', 'Account Id', 'Quantity', 'Total Price Cents', 'Error Type', 'page_name'] as const,
      optionalProperties: ['Billing System'] as const,
    },
  },
  // Event names and property shapes match the Ember sending-domain settings
  // flow (ui repo, app/components/entri-dns-setup) so both surfaces report into
  // the same Mixpanel events. Fired from the shared Entri setup hooks, so they
  // cover both the legacy builders-setup and the sandbox onboarding domain steps.
  email: {
    domainSetupStarted: {
      name: 'Email: Deliverability Domain Setup Started',
      properties: ['Domain', 'Setup Type'] as const,
    },
    domainSetupCompleted: {
      name: 'Email: Deliverability Domain Setup Completed',
      properties: ['Domain', 'Provider'] as const,
    },
    domainSetupAbandoned: {
      name: 'Email: Deliverability Domain Setup Abandoned',
      properties: ['Domain', 'Provider', 'Last Status', 'Setup Type'] as const,
    },
    // Design Studio dashboard-port event (CON-7496) — parcel's
    // `email.created`, fired by the file-upload flow.
    created: {
      name: 'Design Studio: Email Created',
      properties: ['id', 'path', 'source', 'sourceEmailId', 'editorType', 'isTemplate', 'startFrom'] as const,
    },
    // Parcel's `email.deleted` — parcel fires it from its Overmind
    // `deleteNode` action (editor file tree); the dashboard's copy fires from
    // the node context menu (CON-7986).
    deleted: {
      name: 'Design Studio: Email Deleted',
      properties: ['id', 'path'] as const,
    },
  },
  workspaceSetup: {
    clickedChannel: {
      name: 'Workspace Setup: Clicked channel',
      properties: ['account_id', 'channel'] as const,
    },
    clickedDataImport: {
      name: 'Workspace Setup: Clicked data import',
      properties: ['account_id'] as const,
    },
    recommendedNextStep: {
      name: 'Workspace Setup: Clicked recommended next step',
      properties: ['account_id', 'step'] as const,
    },
    viewedAddPeople: {
      name: 'Workspace Setup: Viewed add people',
      properties: [] as const,
    },
    clickedAddPeopleAction: {
      name: 'Workspace Setup: Clicked add people action',
      properties: ['account_id', 'actionName'] as const,
    },
    clickedAddAndVerifyDomain: {
      name: 'Workspace Setup: Clicked add and verify sending domain',
      properties: ['account_id', 'source'] as const,
    },
    clickedAddFromAddress: {
      name: 'Workspace Setup: Clicked add a From address',
      properties: ['account_id', 'source'] as const,
    },
    clickedAddFunds: {
      name: 'Workspace Setup: Clicked add funds',
      properties: ['account_id', 'source'] as const,
    },
  },
  test_delivery_mode: {
    popover_opened: {
      name: 'Test Delivery Mode: Popover Opened',
      properties: ['Route Name'],
    },
    popover_cta_clicked: {
      name: 'Test Delivery Mode: Popover CTA Clicked',
      properties: ['Route Name', 'Action Name'],
    },
  },
  experimentalFeatures: {
    pageViewed: {
      name: 'Experimental Features: Page Viewed',
      properties: [] as const,
    },
    featureToggled: {
      name: 'Experimental Features: Feature Toggled',
      properties: ['Feature', 'Toggled On'] as const,
    },
  },
  apiKeys: {
    pageViewed: {
      name: 'API Keys: Page Viewed',
      properties: ['Tab', 'Context'] as const,
    },
    serviceAccountCreated: {
      name: 'API Keys: Service Account Created',
      properties: [] as const,
    },
    serviceAccountDeleted: {
      name: 'API Keys: Service Account Deleted',
      properties: [] as const,
    },
    pipelineKeyCreated: {
      name: 'API Keys: Pipeline Key Created',
      properties: ['Source Name'] as const,
    },
    pipelineKeyDeleted: {
      name: 'API Keys: Pipeline Key Deleted',
      properties: ['Source Name'] as const,
    },
    signingKeyRegenerated: {
      name: 'API Keys: Signing Key Regenerated',
      properties: [] as const,
    },
  },
  // Data-warehouse sync (FPS-2317). Wire names and payload keys mirror the Ember
  // events so the existing dashboards keep resolving. `Type` is the destination's
  // display name, not its id — that is what Ember sent.
  warehouse: {
    /** The connected config view. Ember fired this from its route-transition map. */
    settingsViewed: {
      name: 'Data Warehouse: Settings Viewed',
      properties: ['Type'] as const,
    },
    /** The same view before a sync exists — the blank slate is its own funnel step. */
    emptyStateViewed: {
      name: 'Data Warehouse: Empty State Viewed',
      properties: ['Type'] as const,
    },
    /** One of the three connector landing pages. */
    destinationViewed: {
      name: 'Data Warehouse: Destination Viewed',
      properties: ['Type'] as const,
    },
    exportEnabled: {
      name: 'Data Warehouse: Export Enabled',
      properties: ['Type', 'Export Type'] as const,
    },
    exportDisabled: {
      name: 'Data Warehouse: Export Disabled',
      properties: ['Type', 'Export Type'] as const,
    },
    disconnectSyncCompleted: {
      name: 'Data Warehouse: Disconnect Sync Completed',
      properties: ['Type'] as const,
    },
    updateConfigurationStarted: {
      name: 'Data Warehouse: Update Configuration Started',
      properties: ['Type'] as const,
    },
    updateConfigurationCompleted: {
      name: 'Data Warehouse: Update Configuration Completed',
      properties: ['Type'] as const,
    },
    createSyncStarted: {
      name: 'Data Warehouse: Create Sync Started',
      properties: ['Type'] as const,
    },
    createSyncCompleted: {
      name: 'Data Warehouse: Create Sync Completed',
      properties: ['Type'] as const,
    },
  },
  // API-credentials page analytics mirror the Ember events (for dashboard
  // continuity): the same events live under `integration` (account level) and
  // `workspace` (workspace level), differing only in the wire-name prefix.
  integration: {
    // Directory card click. `Integration Sub-Category` (subtitle) and
    // `Integration Status` (enabled/disabled) are only sent when applicable.
    clicked: {
      name: 'Integration: Clicked',
      properties: ['Source', 'Integration Name', 'Integration Category', 'Integration Type'] as const,
      optionalProperties: ['Integration Sub-Category', 'Integration Status'] as const,
    },
    // Directory search + category filtering (the type filter fires nothing).
    listSearchedOrFiltered: {
      name: 'Integration: List Searched or Filtered',
      properties: ['Source', 'List Searched', 'List Filtered by Category', 'List Filtered by Type'] as const,
      optionalProperties: ['Integration Category', 'Integration Type', 'Integration Search Value'] as const,
    },
    apiCredentialsCopied: {
      name: 'Integration: API Credentials Copied',
      properties: ['API Credential Type', 'API Credential Copied'] as const,
    },
    apiCredentialsCreationStarted: {
      name: 'Integration: API Credentials Creation Started',
      properties: ['API Credential Type'] as const,
    },
    apiCredentialsDeletionStarted: {
      name: 'Integration: API Credentials Deletion Started',
      properties: ['API Credential Type'] as const,
    },
    trackingApiKeysViewed: {
      name: 'Integration: Tracking API Keys Viewed',
      properties: [] as const,
    },
    appApiKeysViewed: {
      name: 'Integration: App API Keys Viewed',
      properties: [] as const,
    },
    // Reporting-webhook events. `Integration Sub-Category` is part of the wire
    // contract for the enable/disable pair but the reporting-webhook call sites
    // never send it, so it stays optional here.
    //
    // `Integration Disabled or Deleted` is deliberately optional and only sent
    // when the action empties the workspace: disabling the last *enabled*
    // webhook, or deleting the only webhook.
    //
    // Enable Started has two shapes. The list row and the form's state toggle
    // name the surface they fired from. Opening the create page also fires it,
    // for a webhook that does not exist yet — so that one carries no source, and
    // names the transition only when the workspace is getting its very first
    // webhook. Everything beyond the integration name and category is therefore
    // optional.
    //
    // None of the three carries `Webhook Endpoint ID`: Ember's `enableStarted`
    // transform drops the id it is handed, so this event has never identified an
    // endpoint. `Integration: Disable Started` does carry one.
    reportingWebhookEnableStarted: {
      name: 'Integration: Enable Started',
      properties: ['Integration Name', 'Integration Category'] as const,
      optionalProperties: ['Integration Sub-Category', 'Integration Enabled or Re-Enabled', 'Source'] as const,
    },
    reportingWebhookDisableStarted: {
      name: 'Integration: Disable Started',
      properties: [
        'Integration Name',
        'Integration Category',
        'Webhook Endpoint ID',
        'Webhook Endpoint Disabled or Deleted',
        'Source',
      ] as const,
      optionalProperties: ['Integration Sub-Category', 'Integration Disabled or Deleted'] as const,
    },
    reportingWebhookEditStarted: {
      name: 'Integration: Edit Started',
      properties: ['Integration Name', 'Integration Category', 'Webhook Endpoint ID', 'Source'] as const,
    },
    reportingWebhookKeyCopied: {
      name: 'Integration: Reporting Webhook Key Copied',
      properties: ['Webhook Endpoint ID', 'Source'] as const,
    },
    // Fires on both outcomes of a "Send test". `Test Response` carries the server's error detail
    // and is only present on a failure, so it is optional rather than sent empty on success.
    reportingWebhookTestSent: {
      name: 'Integration: Reporting Webhook Test Sent',
      properties: ['Test Passed', 'Webhook Endpoint ID'] as const,
      optionalProperties: ['Test Response'] as const,
    },
  },
  // Workspace-level variant of the api-credentials events. A dedicated category
  // (rather than the shared `workspace` one) keeps these required-prop events out
  // of the dynamic `track('workspace', …)` call sites elsewhere.
  workspaceApiCredentials: {
    apiCredentialsCopied: {
      name: 'Workspace: API Credentials Copied',
      properties: ['API Credential Type', 'API Credential Copied'] as const,
    },
    apiCredentialsCreationStarted: {
      name: 'Workspace: API Credentials Creation Started',
      properties: ['API Credential Type'] as const,
    },
    apiCredentialsDeletionStarted: {
      name: 'Workspace: API Credentials Deletion Started',
      properties: ['API Credential Type'] as const,
    },
    trackingApiKeysViewed: {
      name: 'Workspace: Tracking API Keys Viewed',
      properties: [] as const,
    },
    appApiKeysViewed: {
      name: 'Workspace: App API Keys Viewed',
      properties: [] as const,
    },
  },
  multiAccount: {
    viewedAccounts: {
      name: 'Multi-Account: User Viewed Accounts',
      properties: ['Session Count'] as const,
    },
    switchedAccounts: {
      name: 'Multi-Account: User Switched Accounts',
      properties: ['Session Count', 'Current Account ID', 'New Account Name'] as const,
    },
    clickedAddAccount: {
      name: 'Multi-Account: User Clicked Add Account',
      properties: ['Session Count'] as const,
    },
    loggedOutAccount: {
      name: 'Multi-Account: User Logged Out Of Account',
      properties: ['Session Count', 'Logged-Out Account Name', 'Was Current'] as const,
    },
  },
  partner: {
    // Top-of-funnel intention (opening the create form). The completed create is
    // tracked server-side as "Partner: Account Created" (services), so the gap
    // between the two is form abandonment + backend failures.
    createAccountClicked: {
      name: 'Partner: Create Account Clicked',
      properties: [] as const,
    },
    accountOpened: {
      name: 'Partner: Client Account Opened',
      properties: ['Account ID'] as const,
    },
  },
  googleLogins: {
    reviewViewed: {
      name: 'Google Logins: Duplicate Review Viewed',
      properties: ['Duplicate Count'] as const,
    },
  },
  import: {
    uploadStarted: {
      name: 'Import: Upload Started',
      properties: ['source'] as const,
      optionalProperties: ['dragAndDrop'] as const,
    },
  },
  collections: {
    creationStarted: {
      name: 'Collections: Creation Started',
      properties: [] as const,
    },
    listViewed: {
      name: 'Collections: List Viewed',
      properties: [] as const,
    },
    deleteStarted: {
      name: 'Collections: Delete Started',
      properties: ['Collection ID', 'Collection Size'] as const,
    },
    updateStarted: {
      name: 'Collections: Update Started',
      properties: ['Collection ID', 'Collection Size'] as const,
    },
  },
  account: {
    verificationReviewInboxLinkClicked: {
      name: 'Account: Verification Review Inbox Link Clicked',
      properties: ['MX Provider', 'Email Domain'] as const,
    },
    teamMemberInvitationResent: {
      name: 'Account: Team Member Invitation Resent',
      properties: ['teamMemberEmail'] as const,
    },
    guestInvitationResent: {
      name: 'Account: Guest Invitation Resent',
      properties: ['guestEmail'] as const,
    },
    teamMemberChangedToGuest: {
      name: 'Account: Team Member Changed To Guest',
      properties: ['teamMemberEmail', 'guestEmail'] as const,
    },
    permissionsMatched: {
      name: 'Account: Permissions Matched',
      properties: ['is_guest', 'matched_role'] as const,
    },
    guestInvited: {
      name: 'Account: Guest Invited',
      properties: [] as const,
    },
    cancellation_started: {
      name: 'Account: Cancellation Started',
      properties: [] as const,
    },
    offer_help_continue_to_cancel: {
      name: 'Account: Offer Help Continue To Cancel',
      properties: [] as const,
    },
    offer_help_get_help: {
      name: 'Account: Offer Help Get Help',
      properties: [] as const,
    },
    billing_receipt_download: {
      name: 'Account: Billing History Downloaded',
      properties: [] as const,
    },
    billing_invoice_download: {
      name: 'Account: Billing Invoice Downloaded',
      properties: [] as const,
    },
    retention_discount_offered: {
      name: 'Account: Retention Discount Offered',
      properties: [] as const,
    },
    retention_discount_claimed: {
      name: 'Account: Retention Discount Claimed',
      properties: [] as const,
    },
    retention_discount_declined: {
      name: 'Account: Retention Discount Declined',
      properties: [] as const,
    },
    builder_downgrade_offered: {
      name: 'Account: Builder Downgrade Offered',
      properties: [] as const,
    },
    builder_downgrade_declined: {
      name: 'Account: Builder Downgrade Declined',
      properties: [] as const,
    },
    builder_downgrade_confirm_viewed: {
      name: 'Account: Builder Downgrade Confirm Viewed',
      properties: [] as const,
    },
    builder_downgrade_accepted: {
      name: 'Account: Builder Downgrade Accepted',
      properties: [] as const,
    },
    // Keep this property name. The reactivation funnel reports on it, and a new name loses that history.
    canceled_state_viewed: {
      name: 'Account: Canceled State Viewed',
      properties: ['Reactivate Option Shown'] as const,
    },
    reactivation_started: {
      name: 'Account: Reactivation Started',
      properties: [] as const,
    },
    reactivation_completed: {
      name: 'Account: Reactivation Completed',
      properties: [] as const,
    },
    requireTwoFactorEnableStarted: {
      name: 'Account: Enable Require 2FA Started',
      properties: [] as const,
    },
    requireTwoFactorDisableStarted: {
      name: 'Account: Disable Require 2FA Started',
      properties: [] as const,
    },
    securitySettingsViewed: {
      name: 'Account: Security Settings Viewed',
      properties: [] as const,
    },
    settings_viewed: {
      name: 'Account: Settings Viewed',
      properties: [] as const,
    },
    your_plan_viewed: {
      name: 'Account: Your Plan Viewed',
      properties: [] as const,
    },
    billing_history_viewed: {
      name: 'Account: Billing History Viewed',
      properties: [] as const,
    },
    available_plans_viewed: {
      name: 'Account: Available Plans Viewed',
      properties: [] as const,
    },
    payment_methods_viewed: {
      name: 'Account: Payment Methods Viewed',
      properties: [] as const,
    },
    configureSsoViewed: {
      name: 'Account: Configure SSO Viewed',
      properties: [] as const,
    },
    authenticateSsoStarted: {
      name: 'Account: Authenticate SSO Started',
      properties: ['Identity Provider'] as const,
    },
    authenticateSsoSubmitted: {
      name: 'Account: Authenticate SSO Submitted',
      // 'Metadata URL' only carries a value for SAML; Ember's shared
      // `ssoIdentityProvider` transform emitted the key on every provider.
      properties: ['Identity Provider', 'Metadata URL'] as const,
    },
    authenticateSsoFailed: {
      name: 'Account: Authenticate SSO Failed',
      properties: ['Identity Provider'] as const,
    },
    disableSsoStarted: {
      name: 'Account: Disable SSO Started',
      properties: ['Identity Provider'] as const,
    },
    samlSpCertDownloadSuccess: {
      name: 'Account: SAML SSO SP Certificate Downloaded',
      properties: [] as const,
    },
    samlSpCertDownloadFailed: {
      name: 'Account: SAML SSO SP Certificate Download Failed',
      properties: [] as const,
    },
  },
  salesforce: {
    syncConfigTabChanged: {
      name: 'Salesforce: Sync Config Tab Changed',
      properties: ['source_id', 'record_type', 'workspace_id', 'from_tab', 'to_tab'] as const,
    },
    queryValidationAttempted: {
      name: 'Salesforce: Query Validation Attempted',
      properties: ['source_id', 'sobject_name', 'workspace_id', 'where_clause_length'] as const,
    },
    queryValidationFailed: {
      name: 'Salesforce: Query Validation Failed',
      properties: [
        'source_id',
        'sobject_name',
        'workspace_id',
        'where_clause_length',
        'error_type',
        'error_message',
      ] as const,
    },
    queryValidationSucceeded: {
      name: 'Salesforce: Query Validation Succeeded',
      properties: ['source_id', 'sobject_name', 'workspace_id', 'where_clause_length', 'record_count'] as const,
    },
    filterButtonClicked: {
      name: 'Salesforce: Filter Button Clicked',
      properties: ['source_id', 'record_type', 'workspace_id', 'button_location'] as const,
    },
  },
  sources: {
    syncSetupCompleted: {
      name: 'Sources: Sync Setup Completed',
      properties: [
        'workspace_id',
        'integration_name',
        'source_id',
        'record_type',
        'transformation_type',
        'setup_flow',
        'template',
        'completion_type',
        'destinations_count',
      ] as const,
    },
    syncTemplateOpened: {
      name: 'Sources: Sync Template Opened',
      properties: ['workspace_id', 'integration_name', 'source_id', 'template'] as const,
    },
    syncTemplateAbandoned: {
      name: 'Sources: Sync Template Abandoned',
      properties: ['workspace_id', 'integration_name', 'source_id', 'record_type', 'template'] as const,
    },
  },
  aiQueryGeneration: {
    feedbackPositive: {
      name: 'AI Query Generation: Feedback Positive',
      properties: ['source', 'query', 'request', 'sobject_name'] as const,
    },
    feedbackNegative: {
      name: 'AI Query Generation: Feedback Negative',
      properties: ['source', 'query', 'request', 'sobject_name'] as const,
    },
  },
  snippets: {
    // Property keys mirror the wire payload Ember's `snippets`
    // transforms produce — see `ui/app/utils/analytics/transforms/snippets.ts`.
    listViewed: {
      // Lives under `workspace.snippets_viewed` in the Ember `events` map; kept
      // here so the snippet-page tracking is colocated. Wire name is unchanged.
      name: 'Workspace: Snippets Viewed',
      properties: [] as const,
    },
    creationStarted: {
      name: 'Snippet: Creation Started',
      properties: [] as const,
    },
    listSorted: {
      name: 'Snippet: List Sorted',
      properties: ['Search Term', 'Search Results', 'Sort By', 'Sort Order'] as const,
    },
    listFiltered: {
      name: 'Snippet: List Filtered',
      properties: ['Filtered by Object Types'] as const,
    },
    detailPageViewed: {
      name: 'Snippet: Detail Page Viewed',
      properties: ['Snippet ID', 'Snippet Name', 'Snippet Usage Count Total'] as const,
    },
  },
  assetLibrary: {
    folderViewed: {
      name: 'Asset Library: Folder Viewed',
      properties: ['folder_name', 'folder_id', 'folder_count', 'asset_count'] as const,
    },
    searchStarted: {
      name: 'Asset Library: Search Started',
      properties: ['search_query'] as const,
    },
    pagination: {
      name: 'Asset Library: Pagination',
      properties: ['page', 'total_pages'] as const,
    },
    folderNavigation: {
      name: 'Asset Library: Folder Navigation',
      properties: ['folder_name', 'folder_id'] as const,
    },
    fileSelected: {
      name: 'Asset Library: File Selected',
      properties: ['asset_id', 'file_type', 'folder_name', 'folder_id'] as const,
    },
    folderCreated: {
      name: 'Asset Library: Folder Created',
      properties: ['folder_name', 'parent_folder_id'] as const,
    },
    uploadStarted: {
      name: 'Asset Library: Upload Started',
      properties: ['parent_folder_id', 'file_count'] as const,
    },
    uploadCompleted: {
      name: 'Asset Library: Upload Completed',
      properties: ['asset_id', 'file_type', 'parent_folder_id'] as const,
    },
  },
  assetOptimizer: {
    optimizeImageFile: {
      name: 'Asset Optimizer: Optimize Image File',
      properties: ['Builder Type'] as const,
    },
    optimizeImage: {
      name: 'Asset Optimizer: Optimize Image',
      properties: ['Builder Type', 'original_size'] as const,
    },
    saveOptimizedImage: {
      name: 'Asset Optimizer: Save Optimized Image',
      properties: [
        'view_type',
        'compression_type',
        'retina_mode',
        'Builder Type',
        'original_size',
        'optimized_size',
        'savings_percentage',
      ] as const,
    },
    tryOptimizing: {
      name: 'Asset Optimizer: Try Optimizing',
      properties: ['Builder Type'] as const,
    },
  },
  asset: {
    uploaded: {
      name: 'Asset: Uploaded',
      properties: ['id', 'path'] as const,
    },
  },
  workspace: {
    anonymousEventsMergeEnabled: {
      name: 'Workspace: Anonymous Event Merge Enabled',
      properties: [] as const,
    },
    anonymousEventsMergeDisabled: {
      name: 'Workspace: Anonymous Event Merge Disabled',
      properties: [] as const,
    },
    autoMergeEnabled: {
      name: 'Workspace: Multi-identifier Merge Enabled',
      properties: [] as const,
    },
    autoMergeDisabled: {
      name: 'Workspace: Multi-identifier Merge Disabled',
      properties: [] as const,
    },
    autoMergeOnUpdateEnabled: {
      name: 'Workspace: Auto-merge On Identifier Update Enabled',
      properties: [] as const,
    },
    autoMergeOnUpdateDisabled: {
      name: 'Workspace: Auto-merge On Identifier Update Disabled',
      properties: [] as const,
    },
    preserveUnsubscribesOnMergeEnabled: {
      name: 'Workspace: Preserve Unsubscribes On Merge Enabled',
      properties: [] as const,
    },
    preserveUnsubscribesOnMergeDisabled: {
      name: 'Workspace: Preserve Unsubscribes On Merge Disabled',
      properties: [] as const,
    },
    comparePlansClicked: {
      name: 'Workspace: Compare Plans From Workspaces Viewed',
      properties: ['type'] as const,
    },
    deliverySettingsUpdated: {
      name: 'Workspace: Delivery Settings Updated',
      properties: ['Previous Delivery Settings', 'Delivery Settings'] as const,
    },
    deliveryEmailUpdated: {
      name: 'Workspace: Delivery Email Updated',
      properties: [] as const,
    },
    openTrackingEnabled: {
      name: 'Workspace: Open Tracking Enabled',
      properties: [] as const,
    },
    openTrackingDisabled: {
      name: 'Workspace: Open Tracking Disabled',
      properties: [] as const,
    },
    updateIdentifiersByIdChanged: {
      name: 'Workspace: Update Identifiers By ID Changed',
      properties: ['Identify by ID'] as const,
    },
    identifierChangeStarted: {
      name: 'Workspace: Identifier Change Started',
      properties: ['Current Identifier Configuration', 'Migrate to'] as const,
    },
    identifierChangeSubmitted: {
      name: 'Workspace: Identifier Change Submitted',
      properties: ['Current Identifier Configuration', 'Migrate to'] as const,
    },
  },
  plan_billing: {
    switchToPremiumButtonClicked: {
      name: 'Plan and Billing: Switch to Premium Button Clicked',
      properties: ['Account Id', 'Feature Name', 'Requester', 'Requester Email', 'Requester User Id'] as const,
    },
    planActionClicked: {
      name: 'Plan and Billing: Plan Action Clicked',
      properties: ['Account Id', 'Current Plan', 'Action'] as const,
    },
    clickedSubmit: {
      name: 'Plan and Billing: Clicked Submit',
      properties: ['Account Id', 'Current Plan', 'Submission Type'] as const,
    },
    buyOrRequestAccessClicked: {
      name: 'Plan and Billing: Buy or Request Access Clicked',
      properties: ['Account Id', 'Current Plan', 'Requested Plan', 'Text'] as const,
    },
    salesOrContractsLetsTalkClicked: {
      name: 'Plan and Billing: Sales or Contracts Lets Talk Clicked',
      properties: ['Button Name'] as const,
    },
    includedInPlanViewed: {
      name: "Plan and Billing: What's included in my plan viewed",
      properties: ['Account Id', 'Requester', 'Requester Email', 'Requester User Id'] as const,
    },
    limitsBannerLearnMoreClicked: {
      name: 'Plan and Billing: Limits Banner Learn More Clicked',
      properties: ['Account Id', 'Current Plan', 'Banner Id'] as const,
    },
    viewBillingHelp: {
      name: 'Plan and Billing: View Billing Help',
      properties: ['Account Id', 'Current Plan'] as const,
    },
    viewAllFeaturesClicked: {
      name: 'Plan and Billing: View all features clicked',
      properties: [
        'Account Id',
        'Requester',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Viewed Feature List',
      ] as const,
    },
  },
  aiCompliancePrompt: {
    created: {
      name: 'AI Compliance Prompt: Created',
      properties: [] as const,
    },
    removed: {
      name: 'AI Compliance Prompt: Removed',
      properties: [] as const,
    },
    edited: {
      name: 'AI Compliance Prompt: Edited',
      properties: [] as const,
    },
  },
  privacy: {
    pageViewed: {
      name: 'Account: Privacy and Data Viewed',
      properties: [] as const,
    },
    questionsViewed: {
      name: 'Account: Privacy and Data FAQ Viewed',
      properties: [] as const,
    },
    enableProductTrackingStarted: {
      name: 'Account: Enable Product Tracking Started',
      properties: [] as const,
    },
    disableProductTrackingStarted: {
      name: 'Account: Disable Product Tracking Started',
      properties: [] as const,
    },
  },
  languages: {
    settingsTipLinkClicked: {
      name: 'Languages: Language Settings Tips and Resources Link Clicked',
      properties: ['Language Attribute', 'Link'] as const,
    },
    feedbackCanceled: {
      name: 'Languages: Feedback Canceled',
      properties: [] as const,
    },
  },
  user: {
    feedbackRequestStarted: {
      name: 'User: Feedback Request Started',
      properties: ['Source'] as const,
    },
    reAuthenticated: {
      name: 'User: Re Authenticated',
      properties: ['Method'] as const,
    },
    settingsViewed: {
      name: 'User: Personal Settings Viewed',
      properties: [] as const,
    },
    settingsEdited: {
      name: 'User: Settings Edited',
      properties: [] as const,
    },
    changeGoogleAccountStarted: {
      name: 'User: Change Google Account Started',
      properties: [] as const,
    },
    unlinkGoogleAccountStarted: {
      name: 'User: Unlink Google Account Started',
      properties: [] as const,
    },
    profileImageAdded: {
      name: 'User: Profile Image Added',
      properties: [] as const,
    },
    profileImageReplaced: {
      name: 'User: Profile Image Replaced',
      properties: [] as const,
    },
    profileImageDeleted: {
      name: 'User: Profile Image Deleted',
      properties: [] as const,
    },
  },
  // Client-side *intent* events only — lifecycle events (created/renamed/
  // deleted/imported) are emitted by the backend (ui_api MixTrack), which is
  // authoritative and covers non-dashboard paths. Firing both would double
  // count. (MBL-1940)
  geofences: {
    geosetCreateStarted: {
      name: 'Geofences: Geoset Create Started',
      properties: ['Entry Point', 'Plan Tier'] as const,
    },
    geofenceCreateStarted: {
      name: 'Geofences: Geofence Create Started',
      properties: ['Entry Point', 'Plan Tier', 'Geoset ID'] as const,
    },
    geofenceDeleteStarted: {
      name: 'Geofences: Geofence Delete Started',
      properties: ['Plan Tier', 'Geoset ID', 'Geofence ID', 'Geofence Name'] as const,
    },
    // Both import flows share one wire name — 'Entry Point' tells them apart —
    // but are separate registry entries because they send different property
    // shapes and the registry requires every listed property from every caller.
    geofenceImportStarted: {
      name: 'Geofences: Import Started',
      properties: [
        'Entry Point',
        'Plan Tier',
        'Geoset ID',
        'File Name',
        'File Size Bytes',
        'Import Mode',
        'File Format',
        'Valid Row Count',
        'Invalid Row Count',
      ] as const,
    },
    // No 'Target New Geoset': the import flow is choose-existing-only
    // (MBL-2119) — new sets are always created from the create flow first.
    geosetImportStarted: {
      name: 'Geofences: Import Started',
      properties: ['Entry Point', 'Plan Tier', 'Geoset ID', 'File Name', 'File Size Bytes', 'Import Mode'] as const,
    },
  },
  // Design Studio onboarding events, ported from parcel's analytics catalog
  // (`app/src/services/analytics/types.ts`) for the welcome-screen port
  // (CON-7496). Only the events the ported welcome screen emits are carried
  // over; the names match parcel so the pipeline sees identical events.
  onboarding: {
    welcomePageClicked: {
      name: 'Design Studio: Welcome Page Clicked',
      properties: ['choice'] as const,
    },
    started: {
      name: 'Design Studio: Onboarding Started',
      properties: [] as const,
    },
    pricingCardButtonClicked: {
      name: 'Onboarding: Pricing Card Button Clicked',
      properties: ['Button Name'] as const,
    },
  },
  inline_contracts_sales: {
    formOpened: {
      name: 'Inline Contracts Sales Form: Opened',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formClosed: {
      name: 'Inline Contracts Sales Form: Closed',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formCancelClicked: {
      name: 'Inline Contracts Sales Form: Cancel Clicked',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formCancelDismissed: {
      name: 'Inline Contracts Sales Form: Cancel Dismissed',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formFieldInput: {
      name: 'Inline Contracts Sales Form: Field Input',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
        'Field Name',
      ] as const,
    },
    formSubmitClicked: {
      name: 'Inline Contracts Sales Form: Submit Clicked',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formSubmitError: {
      name: 'Inline Contracts Sales Form: Submit Error',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formSubmitSuccess: {
      name: 'Inline Contracts Sales Form: Submit Success',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
  },
  // Design Studio dashboard events, ported from parcel's analytics catalog
  // (`app/src/services/analytics/types.ts`) for the dashboard port
  // (CON-7496). Parcel posts these to the same track_event endpoint; only
  // the events the ported hooks emit are carried over.
  dashboard: {
    filetreeAction: {
      name: 'Design Studio: Filetree Action',
      properties: ['action_type', 'filetree_item', 'current_view'] as const,
    },
    changedFilesView: {
      name: 'Design Studio: Changed Files View',
      properties: ['current_view'] as const,
    },
    filetreeSorted: {
      name: 'Design Studio: Filetree Sorted',
      properties: ['sort_column', 'sort_direction', 'current_view'] as const,
    },
  },
  // Design Studio bulk-publish event, ported from parcel's analytics catalog
  // (`app/src/services/analytics/types.ts`) for the dashboard bulk-publish port
  // (CON-7507). Name matches parcel so the pipeline sees identical events.
  bulkPublish: {
    completed: {
      name: 'Design Studio: Bulk Publish Completed',
      properties: ['source', 'message_count', 'successful_count', 'failed_count'] as const,
    },
  },
  folder: {
    created: {
      name: 'Design Studio: Folder Created',
      properties: ['id', 'path'] as const,
    },
    // Parcel's `folder.deleted`, fired by the dashboard delete flow (CON-7986).
    deleted: {
      name: 'Design Studio: Folder Deleted',
      properties: ['id', 'path'] as const,
    },
  },
  // Design Studio created-events, ported from parcel's analytics catalog.
  // Parcel fires these from its Overmind nodes store
  // (`app/src/features/workspace-page/stores/nodes.ts`) on create and
  // duplicate; the dashboard port re-fires them from the create modals and
  // the duplicate hook. Names match parcel so the pipeline sees identical
  // events.
  component: {
    created: {
      name: 'Design Studio: Component Created',
      properties: ['id', 'path'] as const,
    },
    // Parcel's `component.deleted`, fired by the dashboard delete flow (CON-7986).
    deleted: {
      name: 'Design Studio: Component Deleted',
      properties: ['id', 'path'] as const,
    },
  },
  whatsapp: {
    templateCreated: {
      name: 'Design Studio: WhatsApp: Template Created',
      properties: ['id', 'Editor', 'category', 'language'] as const,
    },
  },
  userFeedback: {
    clicked: {
      name: 'Design Studio: User Feedback Clicked',
      properties: ['label', 'metadata', 'journeys_workspace_id', 'journeys_template_id'] as const,
    },
    // Parcel's `userFeedback.submitted` — parcel fired it from its local
    // feedback modal; hydra's shared feedback modal is the submit path now,
    // so tracked feedback buttons fire it on successful submit (CON-7986).
    submitted: {
      name: 'Design Studio: User Feedback Submitted',
      properties: ['label', 'metadata', 'feedback', 'journeys_workspace_id', 'journeys_template_id'] as const,
    },
  },
  // Mirrored verbatim (names + payloads) from parcel's `globalStyles` analytics
  // namespace (`app/src/services/analytics/types.ts`) for the global-styles
  // stores slice (CON-7595). Names match parcel so the pipeline sees identical
  // events. `variantEventProps` (CON-7225) emits the `Variant Type` /
  // `Is Default Variant` / `Variant Count For Variable` dimensions.
  globalStyles: {
    viewed: {
      name: 'Design Studio: Global Styles: Viewed',
      properties: [
        'color_variable_count',
        'font_variable_count',
        'radius_variable_count',
        'spacing_variable_count',
        'text_style_variable_count',
      ] as const,
    },
    addStarted: {
      name: 'Design Studio: Global Styles: Add Started',
      properties: ['variable_type'] as const,
    },
    addCompleted: {
      name: 'Design Studio: Global Styles: Add Completed',
      properties: [
        'variable_type',
        'variable_name',
        'value',
        'Variant Type',
        'Is Default Variant',
        'Variant Count For Variable',
      ] as const,
    },
    updateStarted: {
      name: 'Design Studio: Global Styles: Update Started',
      properties: ['variable_type'] as const,
    },
    updateCompleted: {
      name: 'Design Studio: Global Styles: Update Completed',
      properties: [
        'variable_type',
        'variable_name',
        'value',
        'update_type',
        'Variant Type',
        'Is Default Variant',
        'Variant Count For Variable',
      ] as const,
    },
    saved: {
      name: 'Design Studio: Global Styles: Saved',
      properties: [
        'color_variable_count',
        'font_variable_count',
        'radius_variable_count',
        'spacing_variable_count',
        'text_style_variable_count',
      ] as const,
    },
    variableDeleteStarted: {
      name: 'Design Studio: Global Styles: Variable Delete Started',
      properties: ['variable_type', 'variable_name', 'value'] as const,
    },
    variableDeleteCompleted: {
      name: 'Design Studio: Global Styles: Variable Delete Completed',
      properties: ['variable_type', 'variable_name', 'value', 'Variant Type'] as const,
    },
    discarded: {
      name: 'Design Studio: Global Styles: Discarded',
      properties: [] as const,
    },
  },
  // Mirrored verbatim (names + payloads) from parcel's `aiContext` analytics
  // namespace (`app/src/services/analytics/types.ts`) for the AI-context
  // extraction-flows slice (CON-7595, slice 6). Names match parcel so the
  // pipeline sees identical events. The other parcel `aiContext` consumers
  // (visual editor, builder-start-modal) stay in parcel; only the events the
  // ported global-styles surfaces emit are carried over.
  aiContext: {
    introBannerDismissed: {
      name: 'Design Studio: AI Context: Intro Banner Dismissed',
      properties: [] as const,
    },
    styleGenerationStarted: {
      name: 'Design Studio: AI Context: Style Generation Started',
      properties: [] as const,
    },
    errorBannerDismissed: {
      name: 'Design Studio: AI Context: Error Banner Dismissed',
      properties: [] as const,
    },
    successBannerDismissed: {
      name: 'Design Studio: AI Context: Success Banner Dismissed',
      properties: [] as const,
    },
  },
  // Object-type settings events (FPS-2357). Only `listViewed` fires from the
  // index page; the create/edit-started and business-type events fire from the
  // new/edit flows shipped in follow-up PRs. `businessTypeSelfSelected` keeps the
  // 'User:' wire-name prefix the Ember catalog used.
  objects: {
    listViewed: {
      name: 'Object Type: List Viewed',
      properties: ['Has Enabled Objects', 'Has Objects Data'] as const,
    },
    predefinedListViewed: {
      name: 'Object Type: Predefined List Viewed',
      properties: ['Has Enabled Objects', 'Has Objects Data'] as const,
    },
    createStarted: {
      name: 'Object Type: Create Started',
      properties: [] as const,
    },
    editStarted: {
      name: 'Object Type: Edit Started',
      properties: [] as const,
    },
    businessTypeSelfSelected: {
      name: 'User: Business Type Self-Selected',
      properties: ['Business Type'] as const,
    },
  },
  // 'Provider' is an `SmsProviderId` ('infobip' | 'sinch' | 'twilio' | 'vonage').
  // Saved fires on the save submission, not on a successful connection — the two
  // counts differ by credentials the provider rejected.
  sms: {
    providerConnectStarted: {
      name: 'SMS: Provider Connect Started',
      properties: ['Provider'] as const,
    },
    providerConnectSaved: {
      name: 'SMS: Provider Connect Saved',
      properties: ['Provider'] as const,
    },
    // Design Studio SMS create, ported from parcel's catalog — parcel fires it
    // from its `create-sms-modal`; the dashboard port fires it from hydra's.
    templateCreated: {
      name: 'Design Studio: SMS: Template Created',
      properties: ['id', 'Editor'] as const,
    },
  },
  // Outbox delivery retry funnel (Ember `message` category `retry_send_*`). Started
  // fires when a bulk retry opens its confirm; Confirmed/Canceled on the outcome. A
  // per-row retry has no confirm step, so it fires only Confirmed. `Override Message
  // Limit` is always false here — the messaging-limit override modal isn't ported.
  message: {
    retrySendStarted: {
      name: 'Message: Retry Send Started',
      properties: ['Selected Message Count', 'Selected Statuses'] as const,
    },
    retrySendConfirmed: {
      name: 'Message: Retry Send Confirmed',
      properties: ['Selected Message Count', 'Selected Statuses', 'Override Message Limit'] as const,
    },
    retrySendCanceled: {
      name: 'Message: Retry Send Canceled',
      properties: ['Selected Message Count', 'Selected Statuses'] as const,
    },
  },
  // Activity Logs page events. Names and properties match the Ember catalog
  // (`ui/app/utils/analytics/events.ts` + `transforms/logs.ts`) so dashboards
  // stay contiguous across the migration (FPS-2304).
  logs: {
    viewed: {
      name: 'Log: Activity Log Viewed',
      properties: ['Activity Tab'] as const,
    },
    moreLogsLoaded: {
      name: 'Log: More Logs Loaded',
      properties: ['Activity Tab'] as const,
    },
    specificActivityViewed: {
      name: 'Log: Activity Log Specific Activity Viewed',
      properties: ['Activity Tab', 'Merged Anonymous Event', 'Merge Activity Type'] as const,
    },
  },
} as const satisfies EventsShape;

export type IEvents = typeof Events;

export type EventProperties<
  Namespace extends keyof IEvents,
  E extends keyof IEvents[Namespace],
> = IEvents[Namespace][E] extends { properties: readonly (infer P)[] } ? (P extends string ? P : never) : never;
